import React, { useState, useEffect } from 'react';
import { 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  XCircle,
  Activity,
  RefreshCw,
  Loader2,
  Calendar,
  BarChart3
} from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import SuperAdminLayout from '@/components/superadmin/SuperAdminLayout';
import { extractErrorMessage } from '@/utils/errorHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminScheduler = () => {
  const { token } = useAuth();
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchHealth = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API}/scheduler/health`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHealth(response.data);
    } catch (err) {
      console.error('Error fetching scheduler health:', err);
      setError(extractErrorMessage(err, 'Failed to fetch scheduler status'));
      // Set default health data on error
      setHealth({
        status: 'error',
        last_run: null,
        logs: [],
        skip_reasons: {}
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
  }, [token]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-6 h-6 text-[#00E87A]" />;
      case 'warning':
        return <AlertCircle className="w-6 h-6 text-yellow-400" />;
      case 'error':
        return <XCircle className="w-6 h-6 text-red-400" />;
      default:
        return <Clock className="w-6 h-6 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'text-[#00E87A]';
      case 'warning':
        return 'text-yellow-400';
      case 'error':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  if (loading) {
    return (
      <SuperAdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-[#00E87A] animate-spin" />
        </div>
      </SuperAdminLayout>
    );
  }

  return (
    <SuperAdminLayout>
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Scheduler Status</h1>
          <p className="text-gray-400 text-sm mt-1">Monitor automated recurring order generation</p>
        </div>
        <button
          onClick={fetchHealth}
          className="mt-4 sm:mt-0 inline-flex items-center gap-2 px-4 py-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2 text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Status Card */}
        <div className="bg-[#161B22] border border-gray-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Status</h2>
            <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center">
              <Activity className="w-5 h-5 text-[#00E87A]" />
            </div>
          </div>
          
          <div className="flex items-center gap-3 mb-4">
            {getStatusIcon(health?.status)}
            <span className={`text-xl font-semibold capitalize ${getStatusColor(health?.status)}`}>
              {health?.status || 'Unknown'}
            </span>
          </div>

          <div className="pt-4 border-t border-gray-800">
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-gray-500" />
              <span className="text-gray-400">Last Run:</span>
              <span className="text-white">{formatDate(health?.last_run)}</span>
            </div>
          </div>
        </div>

        {/* Skip Reasons Breakdown */}
        <div className="bg-[#161B22] border border-gray-800 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Skip Reasons</h2>
            <div className="w-10 h-10 rounded-lg bg-gray-800 flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-[#00E87A]" />
            </div>
          </div>
          
          {health?.skip_reasons && Object.keys(health.skip_reasons).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(health.skip_reasons).map(([reason, count]) => (
                <div key={reason} className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm capitalize">{reason.replace(/_/g, ' ')}</span>
                  <span className="text-white font-medium">{count}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No skip data available</p>
          )}
        </div>

        {/* Quick Stats */}
        <div className="bg-[#161B22] border border-gray-800 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-white mb-4">Summary</h2>
          
          <div className="space-y-4">
            <div>
              <p className="text-gray-400 text-sm">Orders Generated</p>
              <p className="text-white text-2xl font-bold">{health?.orders_generated || 0}</p>
            </div>
            <div>
              <p className="text-gray-400 text-sm">Orders Skipped</p>
              <p className="text-white text-2xl font-bold">{health?.orders_skipped || 0}</p>
            </div>
            <div>
              <p className="text-gray-400 text-sm">Errors</p>
              <p className={`text-2xl font-bold ${(health?.errors || 0) > 0 ? 'text-red-400' : 'text-white'}`}>
                {health?.errors || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Logs */}
      <div className="mt-6 bg-[#161B22] border border-gray-800 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Recent Scheduler Logs</h2>
        
        {health?.logs && health.logs.length > 0 ? (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {health.logs.slice(0, 10).map((log, index) => (
              <div 
                key={index} 
                className="flex items-start gap-3 p-3 bg-[#0D1117] rounded-lg"
              >
                <span className={`mt-0.5 ${
                  log.level === 'error' ? 'text-red-400' :
                  log.level === 'warning' ? 'text-yellow-400' :
                  'text-gray-500'
                }`}>
                  {log.level === 'error' ? <XCircle className="w-4 h-4" /> :
                   log.level === 'warning' ? <AlertCircle className="w-4 h-4" /> :
                   <CheckCircle className="w-4 h-4" />}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm">{log.message}</p>
                  <p className="text-gray-500 text-xs mt-1">{formatDate(log.timestamp)}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Clock className="w-12 h-12 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No scheduler logs available</p>
            <p className="text-gray-500 text-sm mt-1">Logs will appear after the scheduler runs</p>
          </div>
        )}
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminScheduler;
